import { getObjectEntries, getObjectKeys } from '@festina-life/core'
import { buttonStyles } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { FilterCheckbox } from '@festina-life/ui/listsAndTables'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useId, useMemo } from 'react'

import { useApprovalStatusTranslations } from '../../hooks/useApprovalStatusTranslations'
import { ManageHeader } from '../manageHeader/ManageHeader'
import { PreferencesMenu } from '../pareferencesMenu/PreferencesMenu'
import { BlockFilterSection } from './BlockFilterSection'
import type {
  ApprovalStatus,
  ConfigurationItemVisibility,
  FilterStatus,
} from './types'

type Props = {
  filterStatus: FilterStatus
  onFilterStatusChanged: (filterStatus: FilterStatus) => void
}

export const ConfigurationItemsFilterMenu = ({
  filterStatus,
  onFilterStatusChanged,
}: Props) => {
  const translate = useTranslate()
  const filterCheckboxId = useId()
  const approvalStatusTranslations = useApprovalStatusTranslations()

  const { visibleItems, approvalStatus, blockFilter, showEmptyLayers } =
    filterStatus

  const areAllTypesVisible = getObjectKeys(visibleItems).every(
    (itemType) => visibleItems[itemType],
  )
  const areAllApprovalStatusesVisible = getObjectKeys(approvalStatus).every(
    (status) => approvalStatus[status],
  )
  const isSomeFilterSet =
    !areAllTypesVisible ||
    !areAllApprovalStatusesVisible ||
    blockFilter.mode !== 'all' ||
    showEmptyLayers

  const itemTypeTranslations: Record<ConfigurationItemVisibility, string> =
    useMemo(
      () => ({
        parameter: translate('configurationItemsTree.filter.parameter'),
        enum: translate('configurationItemsTree.filter.enum'),
        template: translate('configurationItemsTree.filter.template'),
        library: translate('configurationItemsTree.filter.library'),
        tenant: translate('configurationItemsTree.filter.tenant'),
        glMap: translate('configurationItemsTree.filter.glMap'),
        reconciliationSpec: translate(
          'configurationItemsTree.filter.reconciliationSpec',
        ),
        hideTemplate: translate('configurationItemsTree.filter.hideTemplate'),
        hideGlMap: translate('configurationItemsTree.filter.hideGlMap'),
      }),
      [translate],
    )

  return (
    <PreferencesMenu
      trigger={
        <Icon
          name="Filter"
          className={cx(
            buttonStyles({
              disabled: false,
              isDanger: false,
              isFullWidth: false,
              variant: 'discreet',
              hasCustomPadding: true,
            }),
            'p-1/2 text-primary-500!',
            { 'fill-warning-500 dark:fill-warning-300': isSomeFilterSet },
          )}
        />
      }
      panelClassName={cx('w-sm fixed!')}
    >
      <ManageHeader
        title={
          <div className="flex w-full justify-between items-center uppercase [&_label]:text-sm">
            <FilterCheckbox
              label={translate('configurationItemsTree.filter.operationType')}
              checked={areAllTypesVisible}
              onChange={(nextValue) => {
                onFilterStatusChanged({
                  ...filterStatus,
                  visibleItems: getObjectKeys(visibleItems).reduce(
                    (acc, itemType) => {
                      acc[itemType] = nextValue
                      return acc
                    },
                    {} as Record<ConfigurationItemVisibility, boolean>,
                  ),
                })
              }}
            />
          </div>
        }
        isFaded
      />
      <div className="flex flex-col gap-y-2 mb-4">
        {getObjectEntries(visibleItems).map(([itemType, isVisible]) => (
          <FilterCheckbox
            key={itemType}
            id={itemType}
            label={itemTypeTranslations[itemType]}
            checked={isVisible}
            onChange={() =>
              onFilterStatusChanged({
                ...filterStatus,
                visibleItems: { ...visibleItems, [itemType]: !isVisible },
              })
            }
          />
        ))}
      </div>
      <ManageHeader
        title={
          <div className="flex w-full justify-between items-center [&_label]:text-sm uppercase">
            <FilterCheckbox
              label={translate('configurationItemsTree.filter.approvalStatus')}
              checked={areAllApprovalStatusesVisible}
              onChange={(nextValue) => {
                onFilterStatusChanged({
                  ...filterStatus,
                  approvalStatus: getObjectKeys(approvalStatus).reduce(
                    (acc, status) => {
                      acc[status] = nextValue
                      return acc
                    },
                    {} as Record<ApprovalStatus, boolean>,
                  ),
                })
              }}
            />
          </div>
        }
        isFaded
      />
      <div className="flex flex-col gap-y-2 mb-4">
        {getObjectEntries(approvalStatus).map(([status, isVisible]) => (
          <FilterCheckbox
            key={status}
            id={status}
            label={approvalStatusTranslations[status]}
            checked={isVisible}
            onChange={() =>
              onFilterStatusChanged({
                ...filterStatus,
                approvalStatus: {
                  ...approvalStatus,
                  [status]: !isVisible,
                },
              })
            }
          />
        ))}
      </div>
      <BlockFilterSection
        blockFilter={blockFilter}
        onBlockFilterChanged={(nextBlockFilter) =>
          onFilterStatusChanged({
            ...filterStatus,
            blockFilter: nextBlockFilter,
          })
        }
      />
      <ManageHeader
        title={
          <div className="flex w-full justify-between items-center uppercase [&_label]:text-sm">
            <span>{translate('configurationItemsTree.filter.visibility')}</span>
          </div>
        }
        isFaded
      />
      <div className="flex flex-col gap-y-2 mb-4">
        <FilterCheckbox
          id={filterCheckboxId}
          label={translate('configurationItemsTree.filter.showEmptyLayers')}
          checked={showEmptyLayers}
          onChange={() =>
            onFilterStatusChanged({
              ...filterStatus,
              showEmptyLayers: !showEmptyLayers,
            })
          }
        />
      </div>
    </PreferencesMenu>
  )
}
